import {
  Typography as MuiTypography,
  Divider as MuiDivider,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import StreamflowChartContainer from "../../components/charts/streamflowChart/StreamflowChartContainer";
import RawTable from "../../components/RawTable";
import { getWaterYearRange } from "../fieldDepthProfile/FieldDepthProfile";
import React, { useMemo, useEffect } from "react";
import useFetchGraphData from "../../hooks/useFetchGraphData";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

const { start_date, end_date } = getWaterYearRange();

const PARAMETER_OPTIONS = [
  { label: "Flow", value: "2" },
  { label: "Stage", value: "1901" },
];

const DEFAULTS = {
  parameterid: 2,
  start_date,
  end_date,
  locationid: [],
};

const schema = z.object({
  parameterid: z.coerce.number().default(DEFAULTS.parameterid),
  start_date: z.string().default(DEFAULTS.start_date),
  end_date: z.string().default(DEFAULTS.end_date),
  locationid: z
    .preprocess((v) => {
      if (Array.isArray(v)) return v;
      if (typeof v === "string" && v.length > 0) return v.split(",");
      return [];
    }, z.array(z.string()))
    .default(DEFAULTS.locationid),
});

export default function StreamflowData() {
  const { params, setParams } = useQueryParams(schema);

  const { data: locationsData } = useFetchGraphData(
    {},
    "streamflow-data/locations"
  );

  const locationOptions = useMemo(() => {
    if (!locationsData?.length) return [];
    return locationsData.map((loc) => ({
      label: loc.location_name,
      value: String(loc.locationid),
    }));
  }, [locationsData]);

  // Seed all locations on first load when none are in the URL
  useEffect(() => {
    if (params.locationid.length === 0 && locationOptions.length > 0) {
      setParams((prev) => ({
        ...prev,
        locationid: locationOptions.map((loc) => loc.value),
      }));
    }
  }, [locationOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const locationIdString = params.locationid.join(",");

  const COLUMNS = [
    { title: "Location", field: "location_name" },
    { title: "Parameter", field: "parameter" },
    { title: "Report Date", field: "rdate", type: "date" },
    { title: "Daily Average", field: "daily_average", type: "numeric" },
    { title: "Daily Min", field: "daily_min", type: "numeric" },
    { title: "Daily Max", field: "daily_max", type: "numeric" },
    { title: "Units", field: "result_unit_name" },
  ];

  const FILTERS = useMemo(
    () => [
      {
        type: "select",
        param: "parameterid",
        label: "Parameter",
        default: String(params.parameterid),
        options: PARAMETER_OPTIONS,
      },
      {
        type: "date",
        param: "start_date",
        label: "Start Date",
        default: params.start_date,
      },
      {
        type: "date",
        param: "end_date",
        label: "End Date",
        default: params.end_date,
      },
      {
        type: "auto",
        param: "locationid",
        label: "Locations",
        multi: true,
        default: params.locationid,
        options: locationOptions,
      },
    ],
    [params, locationOptions]
  );

  return (
    <>
      <Helmet title="Streamflow Data" />
      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Streamflow Data
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This page displays <strong>daily streamflow statistics</strong> from
            telemetry stations across the Cherry Creek Basin. View daily
            average, minimum, and maximum values for <strong>Flow</strong> and{" "}
            <strong>Stage</strong> parameters.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Use the filters below to select a parameter, date range, and one or
            more monitoring locations. The chart and data table update
            automatically as you adjust filters.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tip:</strong> The page URL updates with your selections, so
            you can <strong>bookmark</strong> or{" "}
            <strong>share a direct link</strong> to your current view.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm config={FILTERS} onChange={setParams} />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Streamflow Graph
          </Typography>
          <StreamflowChartContainer
            parameterid={params.parameterid}
            locationid={locationIdString}
            startDate={params.start_date}
            endDate={params.end_date}
          />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Graph
          </Typography>
          <Typography variant="body1" gutterBottom>
            This line chart shows <strong>daily average values</strong> for the
            selected parameter at each monitoring location over the chosen date
            range.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Each line represents a different station. Hover over the chart to
            view exact values at a specific date. A vertical blue guide line
            follows your cursor for easy cross-location comparison.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Raw Data Table
          </Typography>
          <RawTable
            endPoint="streamflow-data"
            params={{
              parameterid: params.parameterid,
              locationid: locationIdString,
              start_date: params.start_date,
              end_date: params.end_date,
            }}
            columns={COLUMNS}
          />
        </Section>
      </main>
    </>
  );
}
